import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import {
    getOperateDirection,
    getOperateType,
    useFramework,
    useIsMouseSelect,
    useIsPickStage,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { useResizeCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import { parseToUnit } from '@/utils/unit-kit';
import {
    BaseLayerViewType,
    MAX_STAGE_HEIGHT,
    MIN_STAGE_HEIGHT,
    REFERENCE_LINE_EVENT_NAMES,
    ResizeOperateType,
    type ReferenceLine,
} from '@/version-types';
import { type ReferenceLines } from '../reference-lines';
import { QuickResizeOperate } from './operate/quick-resize-operate';

/**
 * 长画布的舞台调整尺寸
 */
export const ResizeOperate: React.FC = React.memo(() => {
    const { BC } = useResizeCursor(0);
    const { $t } = useLocaleStore();
    const framework = useFramework();
    const isPickStage = useIsPickStage();
    const tipDefaultTxt = $t((v) => v.stageManager.dragHeight);
    const [tipTxt, setTipTxt] = useState(tipDefaultTxt);
    const [operating, setOperating] = useState(false);
    const originHeight = useRef<number>(1);
    const newHeight = useRef<number>(1);
    const [localReferenceLines, setLocalReferenceLines] = useState<ReferenceLine[]>([]);
    const adjustOriginIndex = useRef(0);
    const isMouseSelect = useIsMouseSelect();

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target as HTMLElement;
        const operateType = getOperateType(target);

        if (operateType !== BaseLayerViewType.STAGE_RESIZE) return;
        const direction = getOperateDirection(target);
        if (!direction) return;

        if (e.isStart) {
            adjustOriginIndex.current = framework.stage.getActiveOriginIndex();
            const bg = framework.getBackgroundLayer(adjustOriginIndex.current);
            if (!bg) return;
            originHeight.current = bg.height;
            setOperating(true);
            framework.operate.disablePick();
            document.body.classList.add(BC);

            // 发送舞台尺寸变化前事件
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_START);
            // 保存修改前的参考线数据， 用于历史记录回退
            const referenceLines =
                framework.getInstance<ReferenceLines>('ReferenceLines')?.getReferenceLineData().lines || [];
            setLocalReferenceLines(referenceLines);
        }
        const offset = e.displacementY / framework.stage.getScale();
        let newHeightTmp = Math.max(MIN_STAGE_HEIGHT, Math.floor(originHeight.current + offset));
        if (newHeightTmp > MAX_STAGE_HEIGHT) {
            Message.error($t((v) => v.stageManager.canvasLimitScope));
            newHeightTmp = MAX_STAGE_HEIGHT;
        }
        newHeight.current = newHeightTmp;

        framework
            .getInstance(QuickResizeOperate)
            .doResize(adjustOriginIndex.current, newHeight.current)
            .send(adjustOriginIndex.current, originHeight.current, newHeight.current, e.isStart, e.isEnd);

        const unit = framework.stage.getStageUnit();
        setTipTxt(`${parseToUnit(newHeight.current, unit)}${unit}`);

        if (e.isEnd) {
            framework.operate.enablePick();
            setTipTxt(tipDefaultTxt);
            setOperating(false);
            framework
                .getInstance(QuickResizeOperate)
                .write(
                    adjustOriginIndex.current,
                    originHeight.current,
                    newHeight.current,
                    localReferenceLines,
                    framework.getInstance<ReferenceLines>('ReferenceLines')?.getReferenceLineData().lines || [],
                );
            document.body.classList.remove(BC);

            // 发送舞台尺寸变化后事件
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_END);
        }

        // 发送舞台尺寸调整事件
        if (!e.isEnd && !e.isStart) {
            // 通知参考线同步调整位置， 以保存跟图层位置的相对关系
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZING, {
                newSize: newHeight.current,
                oldSize: originHeight.current,
                operateType: ResizeOperateType.BC,
                stageIndex: framework.convertOriginToStage(adjustOriginIndex.current),
            });
        }
    });

    return (
        <>
            {isPickStage ? <div className="c-quick-stage-resize-border"></div> : null}
            <div
                className={classNames('c-quick-stage-resize-control', {
                    active: operating,
                    disable: isMouseSelect,
                })}
            >
                <div
                    data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                    data-direction={ResizeOperateType.BC}
                    className={classNames('c-quick-stage-resize-area', BC, {
                        active: isPickStage,
                    })}
                    onMouseDown={() => {
                        framework.stage.setIsPickStage(true);
                    }}
                >
                    <div className="c-quick-stage-resize-area-bar"></div>
                </div>
                <div className="c-quick-stage-resize-tip">{tipTxt}</div>
            </div>
        </>
    );
});
